import React, { useEffect, useMemo, useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Cell,
} from "recharts";
import api from "../lib/api";
import { useAuthStore } from "../store/authStore";

// ── Types ─────────────────────────────────────────────────────────────────────
interface Payment {
  id: number;
  amount: string;
  phone_number: string;
  status: "pending" | "completed" | "failed" | "cancelled";
  mpesa_receipt_number: string;
  token_code: string | null;
  units_allocated: string | null;
  created_at: string;
}

type FilterStatus = "all" | "completed" | "pending" | "failed" | "cancelled";

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (n: number) =>
  new Intl.NumberFormat("en-KE", { maximumFractionDigits: 2 }).format(n);

const STATUS_META: Record<string, { label: string; bg: string; text: string; dot: string; icon: React.ReactNode }> = {
  completed: {
    label: "Completed",
    bg: "bg-emerald-50",
    text: "text-emerald-600",
    dot: "bg-emerald-400",
    icon: (
      <svg viewBox="0 0 14 14" fill="none" className="w-3.5 h-3.5">
        <path d="M2.5 7l3 3 6-5.5" stroke="#10b981" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  pending: {
    label: "Pending",
    bg: "bg-amber-50",
    text: "text-amber-600",
    dot: "bg-amber-400",
    icon: (
      <svg viewBox="0 0 14 14" fill="none" className="w-3.5 h-3.5">
        <circle cx="7" cy="7" r="5.5" stroke="#f59e0b" strokeWidth={1.5} />
        <path d="M7 4.5V7l1.5 1.5" stroke="#f59e0b" strokeWidth={1.5} strokeLinecap="round" />
      </svg>
    ),
  },
  failed: {
    label: "Failed",
    bg: "bg-red-50",
    text: "text-red-500",
    dot: "bg-red-400",
    icon: (
      <svg viewBox="0 0 14 14" fill="none" className="w-3.5 h-3.5">
        <path d="M4 4l6 6M10 4l-6 6" stroke="#ef4444" strokeWidth={1.8} strokeLinecap="round" />
      </svg>
    ),
  },
  cancelled: {
    label: "Cancelled",
    bg: "bg-gray-100",
    text: "text-gray-500",
    dot: "bg-gray-400",
    icon: (
      <svg viewBox="0 0 14 14" fill="none" className="w-3.5 h-3.5">
        <path d="M3 7h8" stroke="#9ca3af" strokeWidth={1.8} strokeLinecap="round" />
      </svg>
    ),
  },
};

// ── Custom tooltip ─────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0f2557] text-white text-[12px] px-3 py-2 rounded-xl shadow-xl">
      <p className="font-semibold mb-1 text-sky-200">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color ?? "#fff" }}>
          {p.name}: <span className="font-bold">KES {fmt(p.value)}</span>
        </p>
      ))}
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, iconBg, icon }: {
  label: string; value: string; sub?: string;
  iconBg: string; icon: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${iconBg}`}>
        {icon}
      </div>
      <p className="text-[12.5px] font-medium text-gray-400 mb-0.5">{label}</p>
      <p className="text-[22px] font-bold text-gray-900 leading-tight">{value}</p>
      {sub && <p className="text-[11.5px] text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

// ── Copy button ────────────────────────────────────────────────────────────────
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button
      onClick={copy}
      className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded transition-all ${
        copied ? "text-emerald-500" : "text-gray-300 hover:text-gray-500"
      }`}
      title="Copy"
    >
      {copied ? (
        <svg viewBox="0 0 12 12" fill="none" className="w-3.5 h-3.5">
          <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3">
          <rect x="1" y="3.5" width="7" height="7.5" rx="1.2" stroke="currentColor" strokeWidth={1.2} />
          <path d="M3.5 3.5V2.5A1.2 1.2 0 0 1 4.7 1.3h5.1A1.2 1.2 0 0 1 11 2.5v7A1.2 1.2 0 0 1 9.8 10.7H8.5" stroke="currentColor" strokeWidth={1.2} />
        </svg>
      )}
    </button>
  );
}

// ── Payment row (expanded detail) ─────────────────────────────────────────────
function PaymentRow({ payment }: { payment: Payment }) {
  const [expanded, setExpanded] = useState(false);
  const meta = STATUS_META[payment.status] ?? STATUS_META.cancelled;

  const date = new Date(payment.created_at);
  const dateStr = date.toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" });
  const timeStr = date.toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit" });

  return (
    <>
      <tr
        className="hover:bg-gray-50/70 cursor-pointer transition-colors group"
        onClick={() => setExpanded((v) => !v)}
      >
        {/* Date */}
        <td className="px-5 py-4">
          <p className="text-[13px] font-semibold text-gray-700">{dateStr}</p>
          <p className="text-[11.5px] text-gray-400">{timeStr}</p>
        </td>

        {/* Amount */}
        <td className="px-5 py-4">
          <p className="text-[15px] font-bold text-gray-900">KES {fmt(parseFloat(payment.amount))}</p>
          {payment.units_allocated && (
            <p className="text-[11.5px] text-sky-500 font-medium mt-0.5">
              {parseFloat(payment.units_allocated).toFixed(2)} units
            </p>
          )}
        </td>

        {/* Token */}
        <td className="px-5 py-4">
          {payment.token_code ? (
            <div className="flex items-center">
              <code className="text-[12px] font-mono font-bold text-[#0f2557] bg-[#0f2557]/6 px-2.5 py-1 rounded-lg tracking-wide">
                {payment.token_code}
              </code>
              <CopyButton text={payment.token_code} />
            </div>
          ) : (
            <span className="text-[13px] text-gray-300">—</span>
          )}
        </td>

        {/* Phone */}
        <td className="px-5 py-4">
          <p className="text-[13px] text-gray-600">{payment.phone_number}</p>
        </td>

        {/* Status */}
        <td className="px-5 py-4">
          <span className={`inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1 rounded-full ${meta.bg} ${meta.text}`}>
            {meta.icon}
            {meta.label}
          </span>
        </td>

        {/* Chevron */}
        <td className="px-4 py-4 text-right">
          <svg
            viewBox="0 0 12 12"
            fill="none"
            className={`w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-all ml-auto ${expanded ? "rotate-180" : ""}`}
          >
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </td>
      </tr>

      {/* Expanded detail row */}
      {expanded && (
        <tr className="bg-gradient-to-r from-[#0f2557]/3 to-sky-50/50">
          <td colSpan={6} className="px-5 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: "Transaction ID", value: `#${payment.id}` },
                { label: "M-Pesa Receipt", value: payment.mpesa_receipt_number || "—" },
                { label: "Phone", value: payment.phone_number },
                {
                  label: "Date & Time",
                  value: date.toLocaleString("en-KE", {
                    day: "numeric", month: "long", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  }),
                },
              ].map((item) => (
                <div key={item.label}>
                  <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wide mb-0.5">{item.label}</p>
                  <p className="text-[13px] font-semibold text-gray-700 font-mono">{item.value}</p>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
export default function PaymentsPage() {
  const { user } = useAuthStore();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/payments/history/").then(({ data }) => {
      setPayments(data.results ?? data);
    }).finally(() => setLoading(false));
  }, []);

  // ── Derived stats ────────────────────────────────────────────────────────────
  const completed = payments.filter((p) => p.status === "completed");
  const totalSpent = completed.reduce((s, p) => s + parseFloat(p.amount), 0);
  const totalUnits = completed.reduce((s, p) => s + (p.units_allocated ? parseFloat(p.units_allocated) : 0), 0);
  const avgAmount = completed.length ? totalSpent / completed.length : 0;

  // Monthly spend chart data (last 6 months)
  const monthlyData = useMemo(() => {
    const map: Record<string, number> = {};
    completed.forEach((p) => {
      const key = new Date(p.created_at).toLocaleDateString("en-KE", { month: "short", year: "2-digit" });
      map[key] = (map[key] ?? 0) + parseFloat(p.amount);
    });
    return Object.entries(map)
      .slice(-6)
      .map(([month, total]) => ({ month, total }));
  }, [completed]);

  // Filtered & searched list
  const visible = useMemo(() => {
    return payments.filter((p) => {
      const matchStatus = filter === "all" || p.status === filter;
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        p.phone_number.includes(q) ||
        (p.token_code?.toLowerCase().includes(q) ?? false) ||
        (p.mpesa_receipt_number?.toLowerCase().includes(q) ?? false) ||
        p.amount.includes(q);
      return matchStatus && matchSearch;
    });
  }, [payments, filter, search]);

  const FILTERS: { key: FilterStatus; label: string }[] = [
    { key: "all", label: "All" },
    { key: "completed", label: "Completed" },
    { key: "pending", label: "Pending" },
    { key: "failed", label: "Failed" },
    { key: "cancelled", label: "Cancelled" },
  ];

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="h-10 w-56 rounded-xl bg-gray-100 animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-28 rounded-2xl bg-gray-100 animate-pulse" />)}
        </div>
        <div className="h-64 rounded-2xl bg-gray-100 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold text-gray-900">Payment History</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">
            {completed.length} successful transaction{completed.length !== 1 ? "s" : ""}
          </p>
        </div>
        {/* Search */}
        <div className="relative sm:w-64">
          <svg viewBox="0 0 18 18" fill="none" className="w-4 h-4 text-gray-300 absolute left-3 top-1/2 -translate-y-1/2">
            <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeWidth={1.5} />
            <path d="M12 12l3.5 3.5" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search token, phone, receipt…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-100 rounded-xl text-[13px] text-gray-700 placeholder:text-gray-300 focus:outline-none focus:border-[#0f2557]/30 shadow-sm"
          />
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Spent"
          value={`KES ${fmt(totalSpent)}`}
          sub={`${completed.length} transactions`}
          iconBg="bg-violet-50"
          icon={
            <svg viewBox="0 0 22 22" fill="none" stroke="#7c3aed" strokeWidth={1.7} className="w-5 h-5">
              <path d="M11 2v18M15.5 5.5H8.75a3.25 3.25 0 0 0 0 6.5h4.5a3.25 3.25 0 0 1 0 6.5H6" strokeLinecap="round" />
            </svg>
          }
        />
        <StatCard
          label="Total Units Bought"
          value={`${fmt(totalUnits)}`}
          sub="water units"
          iconBg="bg-sky-50"
          icon={
            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
              <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" fill="#38bdf8" opacity=".85" />
            </svg>
          }
        />
        <StatCard
          label="Average Top-Up"
          value={`KES ${fmt(avgAmount)}`}
          sub="per transaction"
          iconBg="bg-emerald-50"
          icon={
            <svg viewBox="0 0 22 22" fill="none" stroke="#10b981" strokeWidth={1.7} className="w-5 h-5">
              <path d="M3 17l5-8 4 5 3-3 4 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          }
        />
        <StatCard
          label="Failed Payments"
          value={String(payments.filter((p) => p.status === "failed").length)}
          sub="transactions"
          iconBg="bg-red-50"
          icon={
            <svg viewBox="0 0 22 22" fill="none" stroke="#ef4444" strokeWidth={1.7} className="w-5 h-5">
              <circle cx="11" cy="11" r="9" />
              <path d="M11 7v5M11 15v.5" strokeLinecap="round" />
            </svg>
          }
        />
      </div>

      {/* ── Charts row ── */}
      {monthlyData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Spend area chart */}
          <div className="bg-white rounded-2xl p-5 shadow-sm lg:col-span-2">
            <h2 className="text-[15px] font-bold text-gray-800 mb-0.5">Monthly Spend</h2>
            <p className="text-[12px] text-gray-400 mb-4">Your M-Pesa water payments over time</p>
            <ResponsiveContainer width="100%" height={190}>
              <AreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "Afacad Flux, serif" }}
                  axisLine={false} tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "Afacad Flux, serif" }}
                  axisLine={false} tickLine={false}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip content={<ChartTooltip />} />
                <Area
                  type="monotone" dataKey="total" name="Spent"
                  stroke="#0ea5e9" strokeWidth={2.5}
                  fill="url(#spendGrad)" dot={false}
                  activeDot={{ r: 4, fill: "#0ea5e9", stroke: "#fff", strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Status breakdown bar */}
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h2 className="text-[15px] font-bold text-gray-800 mb-0.5">By Status</h2>
            <p className="text-[12px] text-gray-400 mb-4">Transaction breakdown</p>
            <ResponsiveContainer width="100%" height={190}>
              <BarChart
                data={[
                  { name: "Done", value: completed.length },
                  { name: "Pending", value: payments.filter((p) => p.status === "pending").length },
                  { name: "Failed", value: payments.filter((p) => p.status === "failed").length },
                  { name: "Cancelled", value: payments.filter((p) => p.status === "cancelled").length },
                ].filter((d) => d.value > 0)}
                barCategoryGap="30%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "Afacad Flux, serif" }}
                  axisLine={false} tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "Afacad Flux, serif" }}
                  axisLine={false} tickLine={false} allowDecimals={false}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "#f8fafc" }} />
                <Bar dataKey="value" name="Count" radius={[6, 6, 0, 0]}>
                  {[
                    "#10b981", "#f59e0b", "#ef4444", "#9ca3af",
                  ].map((c, i) => <Cell key={i} fill={c} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Filter tabs ── */}
      <div className="flex items-center gap-1.5 bg-white rounded-2xl shadow-sm p-1.5 w-fit">
        {FILTERS.map((f) => {
          const count = f.key === "all" ? payments.length : payments.filter((p) => p.status === f.key).length;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[12.5px] font-semibold transition-all duration-150 ${
                filter === f.key
                  ? "bg-[#0f2557] text-white shadow-sm"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
            >
              {f.key !== "all" && STATUS_META[f.key] && (
                <span className={`w-1.5 h-1.5 rounded-full ${STATUS_META[f.key].dot}`} />
              )}
              {f.label}
              <span className={`text-[11px] px-1.5 py-0.5 rounded-md font-bold ${
                filter === f.key ? "bg-white/20 text-white" : "bg-gray-100 text-gray-400"
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Table ── */}
      {visible.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
            <svg viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth={1.5} className="w-7 h-7">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <path d="M2 10h20" strokeLinecap="round" />
            </svg>
          </div>
          <p className="text-[15px] font-semibold text-gray-400">No payments found</p>
          <p className="text-[12.5px] text-gray-300 mt-1">
            {search ? "Try a different search term" : "Your payment history will appear here"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-50">
                  <th className="text-left px-5 py-3.5 text-[11.5px] font-semibold text-gray-400 uppercase tracking-wide">Date</th>
                  <th className="text-left px-5 py-3.5 text-[11.5px] font-semibold text-gray-400 uppercase tracking-wide">Amount</th>
                  <th className="text-left px-5 py-3.5 text-[11.5px] font-semibold text-gray-400 uppercase tracking-wide">Token</th>
                  <th className="text-left px-5 py-3.5 text-[11.5px] font-semibold text-gray-400 uppercase tracking-wide">Phone</th>
                  <th className="text-left px-5 py-3.5 text-[11.5px] font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {visible.map((p) => (
                  <PaymentRow key={p.id} payment={p} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Table footer */}
          <div className="px-5 py-3.5 border-t border-gray-50 flex items-center justify-between">
            <p className="text-[12.5px] text-gray-400">
              Showing <span className="font-semibold text-gray-600">{visible.length}</span> of{" "}
              <span className="font-semibold text-gray-600">{payments.length}</span> transactions
            </p>
            <div className="flex items-center gap-1 text-[12px] text-gray-400">
              <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5 text-emerald-400">
                <path d="M8 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2z" fill="currentColor" opacity=".2" />
                <path d="M8 5v3l2 2" stroke="#10b981" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Click any row to view details
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
